import { ComponentPortal } from '@angular/cdk/portal';
import {
  ConnectedPosition,
  FlexibleConnectedPositionStrategy,
  HorizontalConnectionPos,
  OriginConnectionPosition,
  Overlay,
  OverlayConnectionPosition,
  OverlayRef,
  VerticalConnectionPos,
} from '@angular/cdk/overlay';
import {
  DestroyRef,
  Directive,
  effect,
  ElementRef,
  HostListener,
  inject,
  InjectionToken,
  input,
  model,
  OnDestroy,
  OnInit,
  Renderer2,
  signal,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { FocusMonitor } from '@angular/cdk/a11y';
import { Platform } from '@angular/cdk/platform';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { isString } from '@kros-sk/utils-fn/string';

import { TooltipComponent } from './tooltip.component';

export function TOOLTIP_DEFAULT_OPTIONS_FACTORY(): TooltipDefaultOptions {
  return {
    showDelay: 0,
    hideDelay: 0,
  };
}

/** Injection token to be used to override the default options for `matTooltip`. */
export const TOOLTIP_DEFAULT_OPTIONS = new InjectionToken<TooltipDefaultOptions>('kros-tooltip-default-options', {
  providedIn: 'root',
  factory: TOOLTIP_DEFAULT_OPTIONS_FACTORY,
});

/** Default `kros-tooltip` options that can be overridden. */
export interface TooltipDefaultOptions {
  /** Default delay when the tooltip is shown. */
  showDelay: number;

  /** Default delay when the tooltip is hidden. */
  hideDelay: number;
}

const MIN_VIEWPORT_TOOLTIP_THRESHOLD = 8;
const UNBOUNDED_ANCHOR_GAP = 8;

export type TooltipPosition = 'left' | 'right' | 'above' | 'above-left' | 'below' | 'before' | 'after';

@Directive({
  selector: '[krosTooltip]',
  exportAs: 'krosTooltip',
})
export class TooltipDirective implements OnInit, OnDestroy {
  readonly #elementRef = inject(ElementRef);
  readonly #overlay = inject(Overlay);
  readonly #platform = inject(Platform);
  readonly #viewContainerRef = inject(ViewContainerRef);
  readonly #renderer = inject(Renderer2);
  readonly #defaultOptions = inject(TOOLTIP_DEFAULT_OPTIONS, { optional: true });
  readonly #focusMonitor = inject(FocusMonitor);
  readonly #destroyRef = inject(DestroyRef);

  /**
   * The content to be shown in the tooltip. Can be a string or a TemplateRef.
   * If falsy, the tooltip will be hidden.
   */
  tooltipText = model.required<undefined | null | string | TemplateRef<void>>({ alias: 'krosTooltip' });

  context = input<unknown>(undefined, { alias: 'krosTooltipContext' });

  tooltipShowManually = input(false);

  tooltipShowAlways = input(false);

  /** Classes to be passed to the tooltip. Supports the same syntax as `ngClass`. */
  tooltipClass = input<string | string[] | Set<string> | { [key: string]: any }>('');

  /**
   * The delay in milliseconds before showing the tooltip after a hover event.
   */
  tooltipShowDelay = input(this.#defaultOptions?.showDelay ?? 0);

  /**
   * The delay in milliseconds before hiding the tooltip after a hide is called.
   */
  tooltipHideDelay = input(this.#defaultOptions?.hideDelay ?? 0);

  /**
   * Whether the tooltip is disabled.
   */
  disabled = input(false, { alias: 'tooltipDisabled' });

  /**
   * The position of the tooltip relative to the host element.
   */
  position = input<TooltipPosition>('above');

  readonly #viewportMargin = MIN_VIEWPORT_TOOLTIP_THRESHOLD;

  #overlayRef: OverlayRef | null = null;
  #portal: ComponentPortal<TooltipComponent> | null = null;
  #tooltipInstance: TooltipComponent | null = null;
  #touchstartTimeout: ReturnType<typeof setTimeout> | undefined;
  #hasFocus = false;
  #canSetAriaLabel = signal(false);

  constructor() {
    effect(() => {
      if (this.disabled()) {
        this.hide(0);
      }
    });

    effect(() => {
      const tooltipText = this.tooltipText();
      const canSetAriaLabel = this.#canSetAriaLabel();

      if (!tooltipText) {
        this.hide(0);
        return;
      }

      this.#updateTooltipMessage(this.context());

      if (canSetAriaLabel) {
        this.#updateAriaLabel();
      }
    });

    effect(() => {
      this.position();
      if (this.#overlayRef) {
        this.#updatePosition(this.#overlayRef);
        this.#tooltipInstance?.show(0);
        this.#overlayRef.updatePosition();
      }
    });

    effect(() => {
      this.#setTooltipClass(this.tooltipClass());
    });
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (this.#shouldProcessMouseEvents()) {
      this.show();
    }
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    if (this.#shouldProcessMouseEvents()) {
      this.hide();
    }
  }

  @HostListener('touchstart')
  onTouchStart(): void {
    if (this.tooltipShowAlways()) return;

    clearTimeout(this.#touchstartTimeout);
    const DEFAULT_LONGPRESS_DELAY = 500;
    this.#touchstartTimeout = setTimeout(() => this.show(), DEFAULT_LONGPRESS_DELAY);
  }

  @HostListener('touchend')
  onTouchEnd(): void {
    if (this.tooltipShowAlways()) return;

    clearTimeout(this.#touchstartTimeout);
    this.hide();
  }

  @HostListener('keydown.escape', ['$event'])
  onEscapePress(e: Event): void {
    if (this.isTooltipVisible()) {
      this.hide();
      e.stopPropagation();
    }
  }

  ngOnDestroy(): void {
    this.#focusMonitor.stopMonitoring(this.#elementRef.nativeElement);
    clearTimeout(this.#touchstartTimeout);
    this.hide();
    this.#overlayRef?.dispose();
  }

  ngOnInit(): void {
    if (this.tooltipShowAlways()) {
      this.show(0);
    }

    this.#canSetAriaLabel.set(!this.#hasAccessibleLabel(this.#elementRef.nativeElement));

    this.#focusMonitor
      .monitor(this.#elementRef.nativeElement, false)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((origin) => {
        if (origin) {
          // handle only keyboard focus
          if (origin === 'keyboard') {
            this.#onKeyboardFocus();
          }
        } else {
          this.#onBlur();
        }
      });
  }

  /**
   * Shows the tooltip with an optional delay.
   * @param delay The delay in milliseconds before showing the tooltip.
   * @param templateContext The context to be passed to the tooltip template.
   */
  show(delay: number = this.tooltipShowDelay(), templateContext: unknown = undefined): void {
    if (this.disabled() || !this.tooltipText() || this.isTooltipVisible()) {
      this.#tooltipInstance?.cancelPendingAnimations();
      return;
    }

    const overlayRef = this.#createOverlay();
    this.#portal = this.#portal || new ComponentPortal(TooltipComponent, this.#viewContainerRef);
    this.#tooltipInstance = overlayRef.attach(this.#portal).instance;
    this.#setTooltipClass(this.tooltipClass());
    this.#updateTooltipMessage(templateContext ?? this.context());
    this.#tooltipInstance.show(delay);
  }

  /**
   * Hides the tooltip with an optional delay.
   * @param delay The delay in milliseconds before hiding the tooltip.
   */
  hide(delay: number = this.tooltipHideDelay()): void {
    const instance = this.#tooltipInstance;
    if (instance) {
      if (instance.isVisible) {
        instance.hide(delay);
      } else {
        instance.cancelPendingAnimations();
        this.#detach();
      }
    }
  }

  /** Returns true if the tooltip is currently visible to the user */
  isTooltipVisible(): boolean {
    return !!this.#tooltipInstance && this.#tooltipInstance.isVisible;
  }

  #onKeyboardFocus(): void {
    this.#hasFocus = true;
    this.show(0);
  }

  #onBlur(): void {
    this.#hasFocus = false;
    if (this.#canHideTooltip()) {
      this.hide();
    }
  }

  #hasAccessibleLabel(element: HTMLElement): boolean {
    return element.hasAttribute('aria-label') || element.hasAttribute('aria-labelledby');
  }

  #shouldProcessMouseEvents(): boolean {
    return !this.tooltipShowManually() && !this.tooltipShowAlways() && this.#platformSupportsMouseEvents();
  }

  #canHideTooltip(): boolean {
    return !this.tooltipShowAlways() && !this.tooltipShowManually() && !this.#hasFocus;
  }

  #createOverlay(): OverlayRef {
    this.#detach();

    if (this.#overlayRef) {
      const existingStrategy = this.#overlayRef.getConfig().positionStrategy as FlexibleConnectedPositionStrategy;

      if (existingStrategy._origin instanceof ElementRef) {
        return this.#overlayRef;
      }
    }

    // Create connected position strategy that listens for scroll events to reposition.
    const positionStrategy = this.#overlay
      .position()
      .flexibleConnectedTo(this.#elementRef)
      .withFlexibleDimensions(false)
      .withViewportMargin(this.#viewportMargin);

    this.#overlayRef = this.#overlay.create({ positionStrategy });
    this.#updatePosition(this.#overlayRef);

    return this.#overlayRef;
  }

  #updatePosition(overlayRef: OverlayRef): void {
    const position = overlayRef.getConfig().positionStrategy as FlexibleConnectedPositionStrategy;
    const origin = this.#getOrigin();
    const overlay = this.#getOverlayPosition();

    position.withPositions([
      this.#addOffset({ ...origin.main, ...overlay.main }),
      this.#addOffset({ ...origin.fallback, ...overlay.fallback }),
    ]);
  }

  #updateTooltipMessage(templateContext: unknown): void {
    if (this.#tooltipInstance) {
      this.#tooltipInstance.context = templateContext;
      this.#tooltipInstance.tooltip = this.tooltipText() ?? null;
      this.#tooltipInstance.markForCheck();
    }
  }

  #setTooltipClass(tooltipClass: string | string[] | Set<string> | { [key: string]: any }): void {
    if (this.#tooltipInstance) {
      this.#tooltipInstance.tooltipClass = tooltipClass;
    }
  }

  #platformSupportsMouseEvents(): boolean {
    return !this.#platform.IOS && !this.#platform.ANDROID;
  }

  #getOrigin(): { main: OriginConnectionPosition; fallback: OriginConnectionPosition } {
    const position = this.position();
    let originPosition: OriginConnectionPosition;

    if (position === 'above' || position === 'below') {
      originPosition = { originX: 'center', originY: position === 'above' ? 'top' : 'bottom' };
    } else if (position === 'before' || position === 'left') {
      originPosition = { originX: 'start', originY: 'center' };
    } else if (position === 'after' || position === 'right') {
      originPosition = { originX: 'end', originY: 'center' };
    } else if (position === 'above-left') {
      originPosition = { originX: 'start', originY: 'top' };
    } else {
      originPosition = { originX: 'start', originY: 'top' };
    }

    const { x, y } = this.#invertPosition(originPosition.originX, originPosition.originY);

    return {
      main: originPosition,
      fallback: { originX: x, originY: y },
    };
  }

  #getOverlayPosition(): { main: OverlayConnectionPosition; fallback: OverlayConnectionPosition } {
    const position = this.position();
    let overlayPosition: OverlayConnectionPosition;

    if (position === 'above') {
      overlayPosition = { overlayX: 'center', overlayY: 'bottom' };
    } else if (position === 'below') {
      overlayPosition = { overlayX: 'center', overlayY: 'top' };
    } else if (position === 'before' || position === 'left') {
      overlayPosition = { overlayX: 'end', overlayY: 'center' };
    } else if (position === 'after' || position === 'right') {
      overlayPosition = { overlayX: 'start', overlayY: 'center' };
    } else if (position === 'above-left') {
      overlayPosition = { overlayX: 'start', overlayY: 'bottom' };
    } else {
      overlayPosition = { overlayX: 'start', overlayY: 'bottom' };
    }

    const { x, y } = this.#invertPosition(overlayPosition.overlayX, overlayPosition.overlayY);

    return {
      main: overlayPosition,
      fallback: { overlayX: x, overlayY: y },
    };
  }

  #addOffset(position: ConnectedPosition): ConnectedPosition {
    const offset = UNBOUNDED_ANCHOR_GAP;

    if (position.originY === 'top') {
      position.offsetY = -offset;
    } else if (position.originY === 'bottom') {
      position.offsetY = offset;
    } else if (position.originX === 'start') {
      position.offsetX = -offset;
    } else if (position.originX === 'end') {
      position.offsetX = offset;
    }

    return position;
  }

  #invertPosition(
    x: HorizontalConnectionPos,
    y: VerticalConnectionPos
  ): {
    x: HorizontalConnectionPos;
    y: VerticalConnectionPos;
  } {
    if (this.position() === 'above' || this.position() === 'below') {
      if (y === 'top') {
        y = 'bottom';
      } else if (y === 'bottom') {
        y = 'top';
      }
    } else {
      if (x === 'end') {
        x = 'start';
      } else if (x === 'start') {
        x = 'end';
      }
    }

    return { x, y };
  }

  #detach(): void {
    if (this.#overlayRef?.hasAttached()) {
      this.#overlayRef.detach();
    }
    this.#tooltipInstance = null;
  }

  #updateAriaLabel(): void {
    if (isString(this.tooltipText()) && this.tooltipText() && !this.disabled()) {
      this.#renderer.setAttribute(this.#elementRef.nativeElement, 'aria-label', this.tooltipText() as string);
    }
  }
}
